import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  ArrowLeft, 
  Search, 
  Filter, 
  Calendar, 
  Star, 
  FileText, 
  ChevronRight,
  Plus,
  SlidersHorizontal,
  Clock,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  ChevronUp,
  ChevronDown,
  Minus,
  Target,
  Award,
  AlertCircle
} from 'lucide-react';


// Progress Chart Component
const ProgressChart = ({ data, onClose }) => {
  if (!data || data.sessions.length === 0) {
    return (
      <Card className="bg-white border-slate-200 mb-6">
        <CardContent className="p-6 text-center">
          <AlertCircle className="h-8 w-8 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Complete more presentations to see your progress chart</p>
        </CardContent>
      </Card>
    );
  }

  const { sessions, summary } = data;
  const maxScore = Math.max(...sessions.map(s => s.overall_score || 0), 100);
  const minScore = Math.min(...sessions.map(s => s.overall_score || 100).filter(s => s > 0), 0);

  const getTrendIcon = () => {
    if (summary.trend === 'improving') return <TrendingUp className="h-4 w-4 text-emerald-500" />;
    if (summary.trend === 'declining') return <TrendingDown className="h-4 w-4 text-rose-500" />;
    return <Minus className="h-4 w-4 text-slate-400" />;
  };

  const getTrendColor = () => {
    if (summary.trend === 'improving') return 'text-emerald-600 bg-emerald-50';
    if (summary.trend === 'declining') return 'text-rose-600 bg-rose-50';
    return 'text-slate-600 bg-slate-50';
  };

  const formatCategoryName = (key) => {
    return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <Card className="bg-white border-slate-200 mb-6">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Your Progress Journey
            </CardTitle>
            <CardDescription style={{ fontFamily: 'Manrope, sans-serif' }}>
              Score trends across your last {sessions.length} presentations
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <ChevronUp className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Trend Summary */}
        <div className="flex items-center gap-4 mb-6 p-4 rounded-lg bg-slate-50">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${getTrendColor()}`}>
            {getTrendIcon()}
            <span className="font-medium capitalize">{summary.trend.replace('_', ' ')}</span>
            {summary.trend_change !== 0 && (
              <span className="text-sm">
                ({summary.trend_change > 0 ? '+' : ''}{summary.trend_change} pts)
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Target className="h-4 w-4" />
            <span>Best: <strong>{summary.best_score}</strong></span>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Award className="h-4 w-4" />
            <span>Avg: <strong>{summary.average_score}</strong></span>
          </div>
        </div>

        {/* Score Line Chart */}
        <div className="mb-6">
          <div className="flex items-end gap-1 h-32 px-2">
            {sessions.map((session, index) => {
              const score = session.overall_score || 0;
              const heightPx = Math.max(10, Math.round((score / 100) * 128));
              const isLatest = index === sessions.length - 1;
              
              return (
                <div 
                  key={session.session_id} 
                  className="flex-1 h-full flex flex-col items-center justify-end group relative"
                >
                  <div 
                    className={`w-full rounded-t transition-all ${
                      isLatest 
                        ? 'bg-[#E07A5F]' 
                        : score >= 80 
                          ? 'bg-emerald-400' 
                          : score >= 60 
                            ? 'bg-amber-400' 
                            : 'bg-rose-400'
                    } group-hover:opacity-80`}
                    style={{ height: `${heightPx}px` }}
                  />
                  
                  {/* Tooltip */}
                  <div className="absolute bottom-full mb-2 hidden group-hover:block z-10">
                    <div className="bg-slate-900 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-lg">
                      <p className="font-medium">{session.topic}</p>
                      <p className="text-slate-300">Score: {score} ({session.grade || 'N/A'})</p>
                      <p className="text-slate-400">{new Date(session.date).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* X-axis labels */}
          <div className="flex justify-between text-xs text-slate-400 mt-2 px-2">
            <span>{sessions.length > 0 ? new Date(sessions[0].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span>
            <span>Latest</span>
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="border-t pt-4">
          <h4 className="text-sm font-medium text-slate-700 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Category Averages
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(summary.category_averages).map(([category, avg]) => {
              if (avg === null) return null;
              const isStrong = category === summary.strongest_category;
              const isWeak = category === summary.weakest_category;
              
              return (
                <div 
                  key={category}
                  className={`p-3 rounded-lg ${
                    isStrong ? 'bg-emerald-50 border border-emerald-200' : 
                    isWeak ? 'bg-rose-50 border border-rose-200' : 
                    'bg-slate-50'
                  }`}
                >
                  <p className="text-xs text-slate-500 mb-1">{formatCategoryName(category)}</p>
                  <p className={`text-lg font-bold ${
                    isStrong ? 'text-emerald-600' : isWeak ? 'text-rose-600' : 'text-slate-700'
                  }`}>
                    {avg}
                    {isStrong && <span className="text-xs ml-1">★</span>}
                    {isWeak && <span className="text-xs ml-1">↑</span>}
                  </p>
                </div>
              );
            })}
          </div>
          
          <div className="flex gap-4 mt-3 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-emerald-500" /> Strongest
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-rose-500" /> Focus Area
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const EvolveHistory = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  
  // Stats
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    avgScore: 0
  });
  
  // Progress chart
  const [progressData, setProgressData] = useState(null);
  const [showProgressChart, setShowProgressChart] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(false);

  useEffect(() => {
    fetchSessions();
    fetchProgressData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [sessions, searchQuery, statusFilter, sortBy]);
  
  const fetchProgressData = async () => {
    try {
      setLoadingProgress(true);
      const response = await api.get('/evolve/sessions/user/progress-history');
      setProgressData(response.data);
    } catch (error) {
      console.error('Failed to fetch progress data:', error);
    } finally {
      setLoadingProgress(false);
    }
  };

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/evolve/sessions');
      const sessionsData = response.data || [];
      setSessions(sessionsData);
      
      // Calculate stats
      const completed = sessionsData.filter(s => s.status === 'analyzed');
      const scores = completed.map(s => s.overall_score).filter(s => s !== null);
      
      setStats({
        total: sessionsData.length,
        completed: completed.length,
        avgScore: scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0
      });
      
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = useCallback(() => {
    let filtered = [...sessions];
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(s => 
        s.topic_name.toLowerCase().includes(query) ||
        s.concept_overview?.toLowerCase().includes(query)
      );
    }
    
    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(s => s.status === statusFilter);
    }
    
    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'highest':
          return (b.overall_score || 0) - (a.overall_score || 0);
        case 'lowest':
          return (a.overall_score || 0) - (b.overall_score || 0);
        case 'name':
          return a.topic_name.localeCompare(b.topic_name);
        default:
          return 0;
      }
    });
    
    setFilteredSessions(filtered);
  }, [sessions, searchQuery, statusFilter, sortBy]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'analyzed': return 'bg-emerald-100 text-emerald-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-amber-100 text-amber-800';
      case 'created': return 'bg-slate-100 text-slate-600';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'analyzed': return 'Completed';
      case 'completed': return 'Pending Analysis';
      case 'in_progress': return 'In Progress';
      case 'created': return 'Draft';
      default: return status;
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-rose-600';
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Today at ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday at ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'long' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-[#F9F8F6]">
        <div className="p-6 lg:p-8 max-w-6xl mx-auto">
          
          {/* Header */}
          <div className="mb-8">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/evolve')}
              className="mb-4 -ml-2 text-slate-600 hover:text-slate-900"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
            
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Session History
                </h1>
                <p className="text-slate-600 mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  View and manage all your presentation sessions
                </p>
              </div>
              <Button 
                onClick={() => navigate('/evolve/new')}
                className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white gap-2"
                data-testid="new-session-btn"
              >
                <Plus className="h-5 w-5" />
                New Session
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-[#E07A5F]/10">
                    <FileText className="h-5 w-5 text-[#E07A5F]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {stats.total}
                    </p>
                    <p className="text-sm text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>Total Sessions</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-100">
                    <Star className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {stats.completed}
                    </p>
                    <p className="text-sm text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>Completed</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-[#81B29A]/10">
                    <TrendingUp className="h-5 w-5 text-[#81B29A]" />
                  </div>
                  <div>
                    <p className={`text-2xl font-bold ${getScoreColor(stats.avgScore)}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {stats.avgScore}%
                    </p>
                    <p className="text-sm text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>Avg Score</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Progress Chart */}
          {showProgressChart && progressData && progressData.sessions.length > 0 ? (
            <ProgressChart 
              data={progressData} 
              onClose={() => setShowProgressChart(false)}
            />
          ) : showProgressChart && !loadingProgress && (
            <Card className="bg-gradient-to-br from-[#E07A5F]/5 to-[#81B29A]/5 border-slate-200 mb-6">
              <CardContent className="p-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-white shadow-sm">
                    <TrendingUp className="h-6 w-6 text-[#E07A5F]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Track Your Improvement
                    </h3>
                    <p className="text-sm text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Complete analyzed presentations to unlock your progress chart
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate('/evolve/new')}
                  variant="outline"
                  className="border-[#E07A5F] text-[#E07A5F] hover:bg-[#E07A5F]/10"
                >
                  Start Practicing
                </Button>
              </CardContent>
            </Card>
          )}

          {!showProgressChart && progressData && progressData.sessions.length > 0 && (
            <Button 
              variant="ghost" 
              className="mb-4 text-slate-600"
              onClick={() => setShowProgressChart(true)}
            >
              <ChevronDown className="h-4 w-4 mr-2" />
              Show Progress Chart
            </Button>
          )}

          {/* Filters */}
          <Card className="bg-white border-slate-200 mb-6">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Search */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search sessions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 border-slate-200"
                    data-testid="search-input"
                  />
                </div>
                
                {/* Status Filter */}
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[160px] border-slate-200" data-testid="status-filter">
                    <Filter className="h-4 w-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="analyzed">Completed</SelectItem>
                    <SelectItem value="completed">Pending Analysis</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="created">Draft</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Sort */}
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[160px] border-slate-200" data-testid="sort-filter">
                    <SlidersHorizontal className="h-4 w-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest First</SelectItem>
                    <SelectItem value="oldest">Oldest First</SelectItem>
                    <SelectItem value="highest">Highest Score</SelectItem>
                    <SelectItem value="lowest">Lowest Score</SelectItem>
                    <SelectItem value="name">Name A-Z</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Refresh */}
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={fetchSessions}
                  className="border-slate-200"
                  data-testid="refresh-btn"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Sessions List */}
          <Card className="bg-white border-slate-200">
            <CardHeader className="border-b border-slate-100">
              <div className="flex items-center justify-between">
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Sessions ({filteredSessions.length})
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="flex items-center gap-4 p-4 border-b border-slate-100">
                      <Skeleton className="h-14 w-20 rounded-lg" />
                      <div className="flex-1">
                        <Skeleton className="h-5 w-48 mb-2" />
                        <Skeleton className="h-4 w-32" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredSessions.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="mx-auto w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                    <FileText className="h-8 w-8 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {sessions.length === 0 ? 'No sessions yet' : 'No matching sessions'}
                  </h3>
                  <p className="text-slate-500 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {sessions.length === 0 
                      ? 'Start your first presentation coaching session'
                      : 'Try adjusting your filters'
                    }
                  </p>
                  {sessions.length === 0 && (
                    <Button 
                      onClick={() => navigate('/evolve/new')}
                      className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create Session
                    </Button>
                  )}
                </div>
              ) : (
                <ScrollArea className="h-[600px]">
                  <div className="divide-y divide-slate-100">
                    {filteredSessions.map((session) => (
                      <div 
                        key={session.id}
                        className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/evolve/session/${session.id}`)}
                        data-testid={`session-row-${session.id}`}
                      >
                        <div className="flex items-center gap-4">
                          {/* Thumbnail */}
                          <div className="w-20 h-14 rounded-lg bg-gradient-to-br from-[#E07A5F]/20 to-[#81B29A]/20 flex items-center justify-center flex-shrink-0">
                            <FileText className="h-6 w-6 text-[#E07A5F]" />
                          </div>
                          
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-slate-900 truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {session.topic_name}
                              </h4>
                              <Badge className={getStatusColor(session.status)} variant="secondary">
                                {getStatusLabel(session.status)}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-500 truncate mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                              {session.concept_overview}
                            </p>
                            <div className="flex items-center gap-4 text-xs text-slate-400">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatDate(session.created_at)}
                              </span>
                              {session.slide_count > 0 && (
                                <span className="flex items-center gap-1">
                                  <FileText className="h-3 w-3" />
                                  {session.slide_count} slides
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Score */}
                          {session.overall_score !== null && session.overall_score !== undefined && (
                            <div className="text-right flex-shrink-0">
                              <p className={`text-2xl font-bold ${getScoreColor(session.overall_score)}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {session.overall_score}%
                              </p>
                              <p className="text-xs text-slate-400" style={{ fontFamily: 'Manrope, sans-serif' }}>Score</p>
                            </div>
                          )}
                          
                          <ChevronRight className="h-5 w-5 text-slate-300 flex-shrink-0" />
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default EvolveHistory;
